import { Helmet } from "react-helmet-async";
import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { ActivationCheckout } from "@/components/activation/ActivationCheckout";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, Loader2, CheckCircle2, Tv, Shield, Zap } from "lucide-react";
import { cn } from "@/utils/functions/cn";
import { packagesApi, devicesApi } from "@/services/api";
import { t, type Lang } from "@/utils/i18n";
import { useLanguage } from "@/components/providers/language-provider";
import { formatPrice } from "@/utils/formatPrice";

interface LocalizedPrice {
  price: number;
  currency: string;
  symbol: string;
}

interface Package {
  id: number;
  name: string;
  price: number;
  localizedPrices?: Record<string, LocalizedPrice> | null;
  schedule: string;
  description: string | null;
  recommended: boolean;
}

// Per-language price override; base `price` is the USD fallback.
function priceDisplay(pkg: Package, lang: string): { text: string; currency: string } {
  const lp = pkg.localizedPrices?.[lang];
  const amount = lp ? lp.price : pkg.price;
  const symbol = lp ? lp.symbol : "$";
  const currency = lp ? lp.currency : "USD";
  return { text: formatPrice(amount, currency, lang, symbol), currency };
}

function hexFromRaw(raw: string): string {
  return raw.replace(/[^a-fA-F0-9]/g, "").toUpperCase();
}

function urlToOctets(mac: string): string[] {
  const hex = hexFromRaw(mac).slice(0, 12);
  return Array.from({ length: 6 }, (_, i) => hex.slice(i * 2, i * 2 + 2));
}

// ── MAC OTP Input ────────────────────────────────────────────────────────────

interface MacOtpInputProps {
  octets: string[];
  onChange: (octets: string[]) => void;
  disabled?: boolean;
  status: "idle" | "checking" | "found" | "not_found";
}

const MacOtpInput = ({ octets, onChange, disabled, status }: MacOtpInputProps) => {
  const refs = useRef<(HTMLInputElement | null)[]>(Array(6).fill(null));

  const updateOctet = useCallback(
    (index: number, value: string) => {
      const next = [...octets];
      next[index] = hexFromRaw(value).slice(0, 2);
      onChange(next);
    },
    [octets, onChange]
  );

  const handleChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const val = hexFromRaw(e.target.value).slice(0, 2);
    updateOctet(index, val);
    if (val.length === 2 && index < 5) {
      refs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && octets[index] === "" && index > 0) {
      refs.current[index - 1]?.focus();
    }
    if (e.key === "ArrowLeft" && index > 0) refs.current[index - 1]?.focus();
    if (e.key === "ArrowRight" && index < 5) refs.current[index + 1]?.focus();
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const hex = hexFromRaw(e.clipboardData.getData("text")).slice(0, 12);
    if (hex.length === 0) return;
    const next = Array.from({ length: 6 }, (_, i) => hex.slice(i * 2, i * 2 + 2));
    onChange(next);
    // focus last filled or last box
    const lastFilled = next.reduce((acc: number, o: string, i: number) => o.length > 0 ? i : acc, -1);
    const focusIdx = Math.min(lastFilled + 1, 5);
    setTimeout(() => refs.current[focusIdx]?.focus(), 0);
  };

  const borderColor = (index: number) => {
    if (octets[index].length === 2) {
      if (status === "found") return "border-emerald-500/60 bg-emerald-500/[0.06]";
      if (status === "not_found") return "border-amber-500/50 bg-amber-500/[0.04]";
      return "border-brand-accent/60 bg-brand-accent/[0.08]";
    }
    return "border-white/[0.1] bg-white/[0.03]";
  };

  return (
    <div className="flex items-center gap-1 sm:gap-2 w-full max-w-full">
      {octets.map((val, i) => (
        <div key={i} className="flex items-center gap-1 sm:gap-2 min-w-0">
          <input
            ref={(el) => { refs.current[i] = el; }}
            type="text"
            inputMode="tel"
            autoComplete="off"
            spellCheck={false}
            maxLength={2}
            value={val}
            disabled={disabled}
            onChange={(e) => handleChange(i, e)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={handlePaste}
            onFocus={(e) => e.target.select()}
            className={cn(
              "w-10 h-12 sm:w-12 sm:h-14 rounded-[10px] sm:rounded-[12px] border-2 text-center font-mono text-sm sm:text-base font-semibold uppercase tracking-widest",
              "text-white outline-none transition-all duration-200",
              "focus:border-brand-accent focus:bg-brand-accent/[0.1] focus:shadow-[0_0_18px_rgba(68,138,255,0.25)]",
              "disabled:opacity-40 disabled:cursor-not-allowed",
              "placeholder:text-white/20",
              borderColor(i)
            )}
            placeholder=""
          />
          {i < 5 && (
            <span className="text-white/25 font-mono text-sm sm:text-lg font-light select-none">:</span>
          )}
        </div>
      ))}
    </div>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────

const PLAN_SCHEDULES: Record<string, string> = {
  annual: "year",
  yearly: "year",
  year: "year",
  monthly: "month",
  month: "month",
  gold: "lifetime",
  lifetime: "lifetime",
};

const ActivationPage = () => {
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const macFromUrl = searchParams.get("mac") || "";
  // Pricing CTAs link here with ?plan=annual|gold; preselect that package.
  const planSchedule = PLAN_SCHEDULES[(searchParams.get("plan") || "").toLowerCase()];

  const [octets, setOctets] = useState<string[]>(() =>
    macFromUrl ? urlToOctets(macFromUrl) : Array(6).fill("")
  );

  const [selectedPackageId, setSelectedPackageId] = useState<number | null>(null);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [packages, setPackages] = useState<Package[]>([]);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [macStatus, setMacStatus] = useState<"idle" | "checking" | "found" | "not_found">("idle");
  const [devicePayed, setDevicePayed] = useState(false);
  const [paidUntil, setPaidUntil] = useState<Date | null>(null);

  const macReadOnly = !!macFromUrl;
  const macHex = octets.join("");
  const macFormatted = octets.join(":"); // XX:XX:XX:XX:XX:XX
  const isValidMac = macHex.length === 12 && octets.every((o) => o.length === 2);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const selectedPkg = packages.find((p) => p.id === selectedPackageId);
  const isFormValid = isValidMac && macStatus === "found" && !devicePayed && acceptTerms && !!selectedPackageId;

  useEffect(() => { loadPackages(); }, []);

  useEffect(() => {
    if (!isValidMac) {
      setMacStatus("idle");
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    let cancelled = false;
    debounceRef.current = setTimeout(async () => {
      setMacStatus("checking");
      try {
        const res = await devicesApi.checkMacStatus(macFormatted);
        if (cancelled) return;
        const device = (res as any).data?.device || res.device;
        setMacStatus(device ? "found" : "not_found");
        // Same rule as the backend's double-charge guard (POST
        // /billing/payment/activation): only a paid plan that hasn't expired
        // blocks checkout. `payed` alone never flips back after expiry, and a
        // free trial must still be able to subscribe.
        const expiry = device?.payed && device.activationExpired
          ? new Date(device.activationExpired)
          : null;
        const hasPaidPlan = !!expiry && expiry.getTime() > Date.now();
        setDevicePayed(hasPaidPlan);
        setPaidUntil(hasPaidPlan ? expiry : null);
      } catch {
        if (cancelled) return;
        setMacStatus("not_found");
      }
    }, 400);
    return () => {
      cancelled = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [macHex]);

  async function loadPackages() {
    try {
      const data = await packagesApi.getWebPackages();
      setPackages(data);
      const fromUrl = data.find((p: Package) => p.schedule === planSchedule);
      const recommended = data.find((p: Package) => p.recommended);
      if (fromUrl) setSelectedPackageId(fromUrl.id);
      else if (recommended) setSelectedPackageId(recommended.id);
      else if (data.length > 0) setSelectedPackageId(data[0].id);
    } catch {
      setPackages([
        { id: 1, name: "Lifetime", price: 12, schedule: "lifetime", description: "Pay once, use forever", recommended: true },
        { id: 2, name: "Annual", price: 6, schedule: "year", description: "Yearly renewal", recommended: false },
      ]);
      setSelectedPackageId(planSchedule === "year" ? 2 : 1);
    } finally {
      setLoadingPackages(false);
    }
  }

  const getScheduleLabel = (schedule: string) => {
    switch (schedule) {
      case "lifetime": return t('activation', 'oneTime', language);
      case "year": return t('activation', 'perYear', language);
      case "month": return t('activation', 'perMonth', language);
      default: return schedule;
    }
  };

  // Package names in the DB are single-language (pt) — derive a localized
  // name from the schedule, falling back to the raw name.
  const getPlanName = (pkg: { name: string; schedule: string }) => {
    switch (pkg.schedule) {
      case "lifetime": return t('activation', 'planLifetime', language);
      case "year": return t('activation', 'planAnnual', language);
      case "month": return t('activation', 'planMonthly', language);
      default: return pkg.name;
    }
  };

  // DB descriptions are single-language (en) — derive them from the schedule.
  const getPlanDescription = (pkg: Package) => {
    switch (pkg.schedule) {
      case "lifetime": return t('activation', 'descLifetime', language);
      case "year": return t('activation', 'descAnnual', language);
      case "month": return t('activation', 'descMonthly', language);
      default: return pkg.description;
    }
  };

  // "Active until DD/MM/YYYY" (MM/DD/YYYY en, DD.MM.YYYY de/uk), or lifetime (stored as +100 years).
  const paidUntilText = (() => {
    if (!paidUntil) return "";
    if (paidUntil.getFullYear() - new Date().getFullYear() > 50) {
      return t('activation', 'alreadyLifetime', language);
    }
    const dd = String(paidUntil.getDate()).padStart(2, "0");
    const mm = String(paidUntil.getMonth() + 1).padStart(2, "0");
    const date = language === "en"
      ? `${mm}/${dd}/${paidUntil.getFullYear()}`
      : language === "de" || language === "uk"
        ? `${dd}.${mm}.${paidUntil.getFullYear()}`
        : `${dd}/${mm}/${paidUntil.getFullYear()}`;
    return t('activation', 'alreadyPaidUntil', language).replace("{date}", date);
  })();


  return (
    <>
      <Helmet>
        <title>{t('activation', 'title', language)} - MoovaTV</title>
        <meta name="description" content={t('activation', 'subtitle', language)} />
      </Helmet>

      {/* One fold: title + steps on the left, the whole flow (MAC → plan → pay) in a single card on the right. */}
      <section className="px-4 sm:px-6 py-6 sm:py-8 lg:py-6 lg:min-h-[calc(100svh-var(--moova-header-height))] lg:flex lg:items-center">
        <div className="w-full max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] gap-5 sm:gap-7 lg:gap-16 items-center">

            {/* ── Left: title, steps, trust ── */}
            <div className="space-y-5 lg:space-y-8">
              <div>
                <h1 className="!text-[30px] sm:!text-[40px] lg:!text-[52px] font-semibold tracking-tight !leading-[1.08] !mt-0 !mb-2.5 sm:!mb-3 font-heading">
                  {t('activation', 'title', language)}
                </h1>
                <p className="text-[15px] sm:text-[17px] text-white/50 leading-relaxed max-w-md">
                  {t('activation', 'subtitle', language)}
                </p>
              </div>

              <ol className="flex flex-col gap-2.5 sm:gap-3 lg:gap-4">
                {[t('activation', 'step1', language), t('activation', 'step2', language), t('activation', 'step3', language)].map((text, i) => (
                  <li key={i} className="flex items-center gap-3 lg:gap-4">
                    <span className="h-8 w-8 lg:h-10 lg:w-10 shrink-0 grid place-items-center rounded-[9px] lg:rounded-[11px] bg-brand-accent/12 border border-brand-accent/25 text-[13px] lg:text-base font-semibold text-brand-accent">
                      {i + 1}
                    </span>
                    <span className="text-[14px] leading-snug text-white/70 lg:text-[16px] lg:text-white/75">{text}</span>
                  </li>
                ))}
              </ol>

              <div className="hidden lg:flex items-center gap-6 text-[13px] text-white/45">
                <span className="flex items-center gap-2"><Tv className="h-4 w-4 text-brand-accent" />{t('activation', 'smartTv', language)}</span>
                <span className="flex items-center gap-2"><Shield className="h-4 w-4 text-brand-accent" />{t('activation', 'securePayment', language)}</span>
                <span className="flex items-center gap-2"><Zap className="h-4 w-4 text-brand-accent" />{t('activation', 'instantActivation', language)}</span>
              </div>
            </div>

            {/* ── Right: MAC → plan → pay ── */}
            <div className="rounded-[20px] border border-white/[0.08] bg-white/[0.035] p-4 sm:p-6 lg:p-7 space-y-4 sm:space-y-5">

              {/* 1 · MAC */}
              <div className="space-y-2.5">
                <label className="block text-[11px] font-medium text-white/50 uppercase tracking-[0.12em]">
                  {t('activation', 'macLabel', language)}
                </label>
                <MacOtpInput
                  octets={octets}
                  onChange={setOctets}
                  disabled={macReadOnly || submitting}
                  status={macStatus}
                />
                <div className="min-h-5">
                  {isValidMac && macStatus === "checking" && (
                    <div className="flex items-center gap-2 text-sm text-white/40">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      <span>{t('activation', 'checking', language)}</span>
                    </div>
                  )}
                  {isValidMac && macStatus === "found" && !devicePayed && (
                    <div className="flex items-center gap-2 text-sm text-emerald-400">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      <span>{t('activation', 'found', language)}</span>
                    </div>
                  )}
                  {isValidMac && macStatus === "found" && devicePayed && (
                    <div className="flex items-start gap-2 text-sm text-emerald-400">
                      <CheckCircle2 className="h-3.5 w-3.5 mt-[3px] flex-shrink-0" />
                      <span>{paidUntilText}</span>
                    </div>
                  )}
                  {isValidMac && macStatus === "not_found" && (
                    <div className="flex items-center gap-2 text-sm text-amber-400">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      <span>{t('activation', 'notFound', language)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* 2 · Plan */}
              <div className="space-y-2.5">
                <label className="block text-[11px] font-medium text-white/50 uppercase tracking-[0.12em]">
                  {t('activation', 'choosePlan', language)}
                </label>
                {loadingPackages ? (
                  <div className="flex justify-center py-6">
                    <Loader2 className="h-5 w-5 animate-spin text-white/30" />
                  </div>
                ) : (
                  <div className="grid gap-2.5">
                    {packages.map((pkg) => {
                      const isSelected = selectedPackageId === pkg.id;
                      return (
                        <button
                          key={pkg.id}
                          type="button"
                          disabled={submitting}
                          onClick={() => setSelectedPackageId(pkg.id)}
                          aria-pressed={isSelected}
                          className={cn(
                            "relative w-full rounded-[14px] border px-3.5 py-3 sm:px-4 sm:py-3.5 text-left transition-all duration-200 flex items-center justify-between gap-3",
                            isSelected
                              ? "border-brand-accent bg-brand-accent/10 shadow-[0_0_24px_rgba(68,138,255,0.08)]"
                              : "border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/[0.12]"
                          )}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div
                              className={cn(
                                "h-[18px] w-[18px] flex-shrink-0 rounded-full border-2 flex items-center justify-center transition-colors",
                                isSelected ? "border-brand-accent" : "border-white/15"
                              )}
                            >
                              {isSelected && <div className="h-2 w-2 rounded-full bg-brand-accent" />}
                            </div>
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                <span className="font-medium text-[15px]">{getPlanName(pkg)}</span>
                                {pkg.recommended && (
                                  <span className="bg-brand-accent text-white text-[9px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wider">
                                    {t('activation', 'popular', language)}
                                  </span>
                                )}
                              </div>
                              {getPlanDescription(pkg) && (
                                <p className="text-[11px] text-white/35 mt-0.5">{getPlanDescription(pkg)}</p>
                              )}
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="text-lg sm:text-xl font-semibold tracking-tight whitespace-nowrap">
                              {priceDisplay(pkg, language).text}
                            </div>
                            <div className="text-[10px] text-white/30 whitespace-nowrap">
                              {getScheduleLabel(pkg.schedule)} · {priceDisplay(pkg, language).currency}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* 3 · Pay */}
              <div className="space-y-3 pt-0.5">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="terms"
                    disabled={submitting}
                    checked={acceptTerms}
                    onCheckedChange={(checked) => setAcceptTerms(checked === true)}
                    className="mt-0.5 border-white/15 data-[state=checked]:bg-brand-accent data-[state=checked]:border-brand-accent"
                  />
                  <Label htmlFor="terms" className="text-xs leading-relaxed text-white/50">
                    {t('activation', 'acceptTerms', language)}{" "}
                    <a
                      href="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white/70 hover:text-white underline underline-offset-2 transition-colors"
                    >
                      {t('activation', 'termsLink', language)}
                    </a>
                  </Label>
                </div>

                <ActivationCheckout
                  mac={macFormatted}
                  packageId={selectedPackageId}
                  language={language}
                  canPay={isFormValid}
                  price={selectedPkg ? priceDisplay(selectedPkg, language).text : undefined}
                  currency={selectedPkg ? priceDisplay(selectedPkg, language).currency : (packages[0] ? priceDisplay(packages[0], language).currency : undefined)}
                  onBusy={setSubmitting}
                />

                <p className="text-white/25 text-[10px] text-center leading-relaxed">
                  {t('activation', 'disclaimer', language)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ActivationPage;
